import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Accordion, ButtonText } from '@morressier/alembic';
import {
  faBell,
  faClock,
  faEdit,
  faHourglassHalf
} from '@fortawesome/pro-regular-svg-icons';

import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { RequestSubmissionDeadlineExtensionModal } from 'src/components/Submission/decisionModals/RequestSubmissionDeadlineExtensionModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { IconBadge } from 'src/components/IconBadge';
import { MY_SUBMISSION_TABS } from 'src/constants';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import ProtectedAction from 'src/components/ProtectedAction';
import { timeFormatDeadlineDate } from '../../../../../utils/date';

export const AuthorTransferOfferedAdditionalInfo = ({
  setActiveTab
}: {
  setActiveTab: (tab: any) => JSX.Element;
}) => {
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    organizationId
  } = useSubmissionData();
  const { workflowData } = workflowResponse;
  const [showRevisionExtensionModal, setShowRevisionExtensionModal] =
    useState<boolean>(false);

  const deadline = workflowData.currentDeadline;
  const isExtensionRequested =
    workflowData.deadlineExtensions?.status === 'requested';
  const daysToDeadline = calculateDaysToDeadline(
    new Date(workflowData.currentDeadline ?? '')
  );

  return (
    <AdditionalInformationWrapper>
      <Accordion
        chakraAccordionProps={{
          width: '100%',
          defaultIndex: 0,
          allowToggle: true
        }}
        items={[
          {
            title: (
              <Flex alignItems={'flex-start'} gap={2}>
                <IconBadge icon={faBell} variant="neutral" />
                <Flex flexDirection={'column'} marginLeft={2}>
                  <Text textStyle="headingItem">Highlights</Text>
                  <Text textStyle="textContext">
                    Review key updates and other relevant information in this
                    phase.
                  </Text>
                </Flex>
              </Flex>
            ),
            content: (
              <Flex flexDirection="column" width="100%" gap={4}>
                <Flex gap={3} alignItems="center">
                  <IconBadge icon={faEdit} variant="attention" />
                  <Text textStyle="headingItem">Transfer Offered</Text>
                  <ButtonText
                    marginLeft="auto"
                    onClick={() => {
                      setActiveTab?.(MY_SUBMISSION_TABS.FEEDBACK);
                    }}
                  >
                    View Feedback
                  </ButtonText>
                </Flex>

                <>
                  <Flex
                    borderTop="1px solid"
                    borderColor="neutralPrimaryBorder"
                  />
                  <Flex width="100%" gap={3} alignItems="start">
                    {isExtensionRequested ? (
                      <>
                        <IconBadge icon={faHourglassHalf} variant="attention" />
                        <Flex flexDirection="column" flex={1}>
                          <Text textStyle="headingItem">
                            Author Transfer Offer Extension Requested
                          </Text>
                        </Flex>
                      </>
                    ) : (
                      <>
                        <IconBadge icon={faClock} variant="attention" />
                        <Flex flexDirection="column" flex={1}>
                          <Text textStyle="headingItem">
                            {deadline
                              ? `Deadline on ${timeFormatDeadlineDate(deadline)}`
                              : null}
                          </Text>
                          <Text textStyle="textContext">
                            {workflowData.deadlineExtensions?.status ===
                            'denied'
                              ? 'The editors have rejected your last extension request.'
                              : `You have ${daysToDeadline} days to submit your work.`}
                            If you decline, or if you cannot decide under the
                            defined deadline, this Submission will be rejected.
                          </Text>
                        </Flex>
                      </>
                    )}
                    <ProtectedAction
                      relation="author"
                      entityType="submission"
                      entityId={submissionId}
                      tooltipLabel="You do not have permission to perform this action"
                    >
                      <ButtonText
                        marginLeft="auto"
                        disabled={isExtensionRequested}
                        onClick={() => setShowRevisionExtensionModal(true)}
                      >
                        Request Extension
                      </ButtonText>
                    </ProtectedAction>
                  </Flex>
                </>
              </Flex>
            )
          }
        ]}
      />

      {showRevisionExtensionModal && (
        <RequestSubmissionDeadlineExtensionModal
          onClose={() => setShowRevisionExtensionModal(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          label="transfer decision"
        />
      )}
    </AdditionalInformationWrapper>
  );
};
